import { register } from '@/js/api/auth.ts';
import { clearField, FIELD_SELECTOR, showFormFieldErrors } from '@/js/fields.ts';
import { normalizePhoneForBackend } from '@/js/helper/clear-phone.ts';
import { getValidationLocaleValue } from '@/js/helper/locale.ts';
import { ERROR_REQUEST_TEXT, getServerErrorMessage, showErrorNotify } from '@/js/notification.ts';

const MIN_PASSWORD_LENGTH = 8;
const EMAIL_REGEX = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/;

const REGISTER_VALIDATION_MESSAGES = {
  nameRequired: getValidationLocaleValue('register_validation_name_required'),
  lastNameRequired: getValidationLocaleValue('register_validation_last_name_required'),
  emailRequired: getValidationLocaleValue('register_validation_email_required'),
  emailInvalid: getValidationLocaleValue('register_validation_email_invalid'),
  phoneRequired: getValidationLocaleValue('register_validation_phone_required'),
  phoneInvalid: getValidationLocaleValue('register_validation_phone_invalid'),
  passwordRequired: getValidationLocaleValue('register_validation_password_required'),
  passwordMin: getValidationLocaleValue('register_validation_password_min'),
  passwordConfirmationMismatch: getValidationLocaleValue('register_validation_password_confirmation_mismatch'),
  personalAgreeRequired: getValidationLocaleValue('register_validation_personal_agree_required'),
};

function clearFormFields(form: HTMLFormElement) {
  const fields = form.querySelectorAll<HTMLElement>(FIELD_SELECTOR);
  for (const field of fields) {
    clearField(field);
  }
}

function getRegisterFieldErrors(formData: FormData, normalizedPhone: string | null) {
  const fieldErrors: Record<string, string> = {};
  const name = formData.get('name')?.toString().trim() ?? '';
  const lastName = formData.get('last_name')?.toString().trim() ?? '';
  const email = formData.get('email')?.toString().trim() ?? '';
  const phone = formData.get('phone')?.toString().trim() ?? '';
  const password = formData.get('password')?.toString() ?? '';
  const passwordConfirmation = formData.get('password_confirmation')?.toString() ?? '';
  const personalAgree = formData.get('personal_agree')?.toString() ?? '';

  if (!name) {
    fieldErrors.name = REGISTER_VALIDATION_MESSAGES.nameRequired;
  }

  if (!lastName) {
    fieldErrors.last_name = REGISTER_VALIDATION_MESSAGES.lastNameRequired;
  }

  if (!email) {
    fieldErrors.email = REGISTER_VALIDATION_MESSAGES.emailRequired;
  }
  else if (!EMAIL_REGEX.test(email)) {
    fieldErrors.email = REGISTER_VALIDATION_MESSAGES.emailInvalid;
  }

  if (!phone) {
    fieldErrors.phone = REGISTER_VALIDATION_MESSAGES.phoneRequired;
  }
  else if (normalizedPhone == null) {
    fieldErrors.phone = REGISTER_VALIDATION_MESSAGES.phoneInvalid;
  }

  if (!password) {
    fieldErrors.password = REGISTER_VALIDATION_MESSAGES.passwordRequired;
  }
  else if (password.length < MIN_PASSWORD_LENGTH) {
    fieldErrors.password = REGISTER_VALIDATION_MESSAGES.passwordMin;
  }

  if (password && password !== passwordConfirmation) {
    fieldErrors.password_confirmation = REGISTER_VALIDATION_MESSAGES.passwordConfirmationMismatch;
  }

  if (personalAgree !== '1') {
    fieldErrors.personal_agree = REGISTER_VALIDATION_MESSAGES.personalAgreeRequired;
  }

  return fieldErrors;
}

export function initRegisterForm() {
  const form = document.querySelector<HTMLFormElement>('.js-form-register');
  if (!form)
    return;

  if (form.dataset.registerFormInitialized === 'true')
    return;

  form.dataset.registerFormInitialized = 'true';
  form.addEventListener('submit', handleRegisterFormSubmit);

  function handleRegisterFormSubmit(event: Event): void {
    event.preventDefault();
    if (!form) {
      return;
    }
    clearFormFields(form);
    void registerFormAsync(form);
  }

  async function registerFormAsync(form: HTMLFormElement): Promise<void> {
    const formData = new FormData(form);
    const phone = formData.get('phone')?.toString() ?? '';
    const normalizedPhone = normalizePhoneForBackend(phone);

    if (showFormFieldErrors(form, getRegisterFieldErrors(formData, normalizedPhone))) {
      return;
    }

    if (normalizedPhone !== null) {
      formData.set('phone', normalizedPhone);
    }

    try {
      const response = await register(formData);
      if (response.data?.success && (response.data.redirect_url != null)) {
        window.location.href = response.data.redirect_url;
      }
      else {
        showErrorNotify({ text: getServerErrorMessage(response.error, ERROR_REQUEST_TEXT) });
      }
    }
    catch (error) {
      showErrorNotify({ text: ERROR_REQUEST_TEXT });
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }
}
